import type { DomApplier } from '../cssFeatures.types'
import type { SelectorObj } from '../../shared.types'
import { getVariantIds, getVariantSelectors } from '../../utils/variantsUtils'
import { getCSSObjectFromTransformationItem } from '@wix/thunderbolt-becky-root'

export const DEFAULT_VARIANT_KEY = ''

export const transformationsDomApplier: DomApplier<'transformations'> = (
	{ id: compId },
	idSelector,
	breakpointId,
	{ transformationsVariants, transformations },
	cssContext
) => {
	if (!transformations) {
		return null
	}

	const {
		breakpointToTransformation,
		ignoredTransformProps,
		defaultRotationInDegrees,
		vhScalingFactor,
	} = transformations

	const getTransformationInBreakpoint = () => breakpointToTransformation?.[breakpointId] || {}

	const getDomSelector = (variantKey: string, shouldAddNotScreenInAnimationSelector: boolean) => {
		const variantSelectors = transformationsVariants
			? getVariantSelectors(variantKey, transformationsVariants, compId, cssContext)
			: []

		const hasHoverVariant = getVariantIds(variantKey).some(
			(variantId) => transformationsVariants?.[variantId]?.type === 'Hover'
		)

		return variantSelectors
			.map((variantSelector) => {
				let domSelector = variantSelector

				if (shouldAddNotScreenInAnimationSelector) {
					domSelector = `${domSelector}:not(.is-animating)`
				}

				const shouldAddFocusA11yRule = hasVisibilityTransformation && hasHoverVariant
				if (shouldAddFocusA11yRule) {
					const focusRule = domSelector.replace(/:hover/g, ':focus-within')
					domSelector = `${domSelector},.keyboard-tabbing-on ${focusRule}`
				}

				return domSelector
			})
			.join(', ')
	}

	const transformationInBreakpoint = getTransformationInBreakpoint()
	const hasVisibilityTransformation = cssContext.visibilityTransformations?.[breakpointId] || false

	return Object.entries(transformationInBreakpoint).reduce<SelectorObj>((acc, [variantKey, transformData]) => {
		const cssFromTransformationItem = getCSSObjectFromTransformationItem(
			transformData,
			ignoredTransformProps,
			defaultRotationInDegrees,
			{
				vhScalingFactor: vhScalingFactor ? vhScalingFactor : undefined,
			}
		)

		if (Object.keys(cssFromTransformationItem).length === 0) {
			return {}
		}

		const isDefaultVariant = variantKey === DEFAULT_VARIANT_KEY
		const shouldAddNotScreenInAnimationSelector =
			isDefaultVariant && !!cssFromTransformationItem['transform-origin']

		const domSelector = getDomSelector(variantKey, shouldAddNotScreenInAnimationSelector)

		acc[domSelector] = Object.assign(acc[domSelector] || {}, cssFromTransformationItem)

		return acc
	}, {})
}
